import React from 'react';

import useGradualIncrement from 'lib/hooks/useGradualIncrement';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';
import type { Location } from "@remix-run/router";

function getSocketParams(location: Location<any>) {
  if (!location) {
    return {};
  }
  const queryString = location?.search ?? '';
  const queryParams: Partial<URLSearchParams> | undefined = queryString !== '' ? new URLSearchParams(queryString) : undefined;

  const paramTab = queryParams?.get ? queryParams.get('tab') : undefined;
  const paramBlockNumber = queryParams?.get ? queryParams.get('block_number') : undefined;
  const paramPage = queryParams?.get ? queryParams.get('page') : undefined;

  if (
    location.pathname === '/txs' &&
    (paramTab === 'validated' || paramTab === undefined) &&
    !paramBlockNumber &&
    !paramPage
  ) {
    return { topic: 'transactions:new_transaction' as const, event: 'transaction' as const };
  }

  if (location.pathname === '/') {
    return { topic: 'transactions:new_transaction' as const, event: 'transaction' as const };
  }

  if (
    location.pathname === '/txs' &&
    paramTab === 'pending' &&
    !paramBlockNumber &&
    !paramPage
  ) {
    return { topic: 'transactions:new_pending_transaction' as const, event: 'pending_transaction' as const };
  }

  return {};
}

function assertIsNewTxResponse(response: unknown): response is { transaction: number } {
  return typeof response === 'object' && response !== null && 'transaction' in response;
}
function assertIsNewPendingTxResponse(response: unknown): response is { pending_transaction: number } {
  return typeof response === 'object' && response !== null && 'pending_transaction' in response;
}

export default function useNewTxsSocket(location: Location<any>) {
  const [ num, setNum ] = useGradualIncrement(0);
  const [ socketAlert, setSocketAlert ] = React.useState('');

  const { topic, event } = getSocketParams(location);

  const handleNewTxMessage = React.useCallback((response: { transaction: number } | { pending_transaction: number } | unknown) => {
    if (assertIsNewTxResponse(response)) {
      setNum(response.transaction);
    }
    if (assertIsNewPendingTxResponse(response)) {
      setNum(response.pending_transaction);
    }
  }, [ setNum ]);

  const handleSocketClose = React.useCallback(() => {
    setSocketAlert('Connection is lost. Please reload page.');
  }, []);

  const handleSocketError = React.useCallback(() => {
    setSocketAlert('An error has occurred while fetching new transactions. Please reload page.');
  }, []);

  const channel = useSocketChannel({
    topic,
    onSocketClose: handleSocketClose,
    onSocketError: handleSocketError,
    isDisabled: !topic,
  });

  useSocketMessage({
    channel,
    event,
    handler: handleNewTxMessage,
  });

  if (!topic && !event) {
    return {};
  }

  return { num, socketAlert };
}
